import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    loadClaimEventStatuses,
    loadClaimEventStatusReasons,
    loadClaimEventTypes
} from '../../../../store/directusService';
import {
    clearClaimQueues,
    loadClaimQueueForecast,
    searchClaimEvents,
    searchClaimQueue
} from '../../../../store/participantService';
import { getNameFromId } from '../../../../utils/directusFunctions';
import { DEFAULT_TABLE_ROWS_PER_PAGE } from '../../../../utils/formValidation/commonConstants';
import { stableSort } from '../../../../utils/sortFunctions';
import ResultsTable from '../../../table/ResultsTable';
import LoadingSpinner from '../../../ui/LoadingSpinner';

import ClaimsForecastingTableRow from './ClaimsForecastingTableRow';
import StatusEditor from './StatusEditor';

import app from '../../../../app.module.css';
import local from '../../requests/requests.module.css';

const headCells = [
    { id: 'claimTypeName', numeric: false, label: 'Claim Type' },
    { id: 'claimQueueStatusName', numeric: false, label: 'Claim Status' },
    { id: 'dueDate', numeric: true, label: 'Due Date' },
    { id: 'eventDate', numeric: true, label: 'Event Date' }
];

const initialRowMetaData = {
    order: 'asc',
    orderBy: 'claimTypeName',
    page: 0,
    rowsPerPage: DEFAULT_TABLE_ROWS_PER_PAGE
};

const ClaimsForecastingTableManagement = () => {
    // HOOKS
    const dispatch = useDispatch();

    // LOCAL STATE
    const [rows, setRows] = useState([]);
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [isStatusEditorOpen, setIsStatusEditorOpen] = useState(false);
    const [statusRow, setStatusRow] = useState({});
    const [claimQueue, setClaimQueue] = useState({});
    const [claimEvent, setClaimEvent] = useState({});
    const [openId, setOpenId] = useState('');

    // STORE STATE
    const claimEventStatuses = useSelector(
        (state) => state.entities.directusService.claimEventStatuses
    );
    const claimEventStatusReasons = useSelector(
        (state) => state.entities.directusService.claimEventStatusReasons
    );
    const claimEventTypes = useSelector((state) => state.entities.directusService.claimEventTypes);
    const claimEvents = useSelector((state) => state.entities.participantService.claimEvents);
    const claimQueueForecast = useSelector(
        (state) => state.entities.participantService.claimQueueForecast
    );
    const claimQueues = useSelector((state) => state.entities.participantService.claimQueues);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);

    // USE EFFECTS
    useEffect(() => {
        setRows([]);
        if (!currentParticipant?.id) return;
        claimEventStatuses?.length < 1 && dispatch(loadClaimEventStatuses());
        claimEventStatusReasons?.length < 1 && dispatch(loadClaimEventStatusReasons());
        claimEventTypes?.length < 1 && dispatch(loadClaimEventTypes());
        dispatch(
            searchClaimEvents({
                eligibilityGroupIds: [currentParticipant.eligibilityGroupId]
            })
        );
        dispatch(loadClaimQueueForecast(currentParticipant.id));
        dispatch(searchClaimQueue({ ptCodes: [currentParticipant.ptCode] }));

        return cleanUp;
    }, [currentParticipant]);

    useEffect(() => {
        if (
            !claimEventTypes?.length ||
            !claimEvents?.length ||
            !claimQueueForecast?.length ||
            (claimQueues?.length && claimQueues[0].ptCode !== currentParticipant.ptCode)
        )
            return;

        const claimQueuesWithEventDisplayName = claimQueues.filter(
            (el) => el.eventTypeDisplayName !== ''
        );
        const claimQueuesWithoutEventDisplayName = claimQueues.filter(
            (el) => el.eventTypeDisplayName === ''
        );
        const emptyClaimEvents = claimEvents.filter((el) => !el.eventTypeId);
        const allClaimQueues = [
            ...claimQueuesWithEventDisplayName,
            ...claimQueuesWithoutEventDisplayName,
            ...emptyClaimEvents
        ];

        const updatedRows = allClaimQueues
            .map((el) => {
                let claim;
                let forecast;

                if (!el?.eligibilityGroupId) {
                    claim = el;

                    forecast = claimQueueForecast.find(
                        (entry) => entry.eventTypeId === el.eventTypeId.toLowerCase()
                    );
                    return {
                        ...el,
                        claimTypeName: el.eventType,
                        claimTypeDisplayName: el?.eventTypeDisplayName
                            ? el.eventTypeDisplayName
                            : el.eventType,
                        claimQueueStatusName: claim?.status || '',
                        dueDate: forecast?.dueDate || '', // for possible claims not necessarily existing.
                        eventDate: claim?.eventDate || '',
                        history: claim?.history || [],
                        claimQueueStatusId: claim?.statusId || '',
                        claimQueueReasonId: claim?.reasonId || '',
                        claimQueueDate: claim?.lastStatusUpdated || '',
                        claimQueueNotes: claim?.notes || '',
                        claimQueueUserId: claim?.userId || ''
                    };
                } else {
                    // There will be duplicates here that we need to remove from the rows
                    const removeDuplicateClaimEventsWithDisplayName =
                        claimQueuesWithEventDisplayName.find(
                            (entry) => el.claimTypeId === entry.eventTypeId
                        );
                    if (removeDuplicateClaimEventsWithDisplayName) return null;

                    const removeDuplicateClaimEventsWithoutDisplayName =
                        claimQueuesWithoutEventDisplayName.find(
                            (entry) => el.claimTypeId === entry.eventTypeId
                        );
                    if (removeDuplicateClaimEventsWithoutDisplayName) return null;

                    claim = claimEvents.find((entry) => entry.claimTypeId === el.claimTypeId);
                    forecast = claimQueueForecast.find(
                        (entry) => entry.eventTypeId === el.claimTypeId
                    );
                    return {
                        ...el,
                        claimTypeName: getNameFromId(claimEventTypes, el.claimTypeId),
                        claimTypeDisplayName: el?.eventTypeDisplayName
                            ? el.eventTypeDisplayName
                            : getNameFromId(claimEventTypes, el.claimTypeId),
                        claimQueueStatusName: claim?.status || '',
                        dueDate: forecast?.dueDate || '', // for possible claims not necessarily existing.
                        eventDate: claim?.eventDate || '',
                        history: claim?.history || [],
                        claimQueueStatusId: claim?.statusId || '',
                        claimQueueReasonId: claim?.reasonId || '',
                        claimQueueDate: claim?.lastStatusUpdated || '',
                        claimQueueNotes: claim?.notes || '',
                        claimQueueUserId: claim?.userId || ''
                    };
                }
            })
            .filter((el) =>
                el?.claimTypeDisplayName ? el.claimTypeDisplayName : el?.claimTypeName
            );
        setRows(updatedRows);
    }, [claimEvents, claimQueueForecast, claimQueues, claimEventTypes]);

    // HELPER FUNCTIONS
    const loadingError = () => {
        if (claimEventStatuses?.length < 1) return 'No claim event statuses found';
        if (claimEvents?.length < 1) return 'No claim events found';
    };

    const cleanUp = () => {
        dispatch(clearClaimQueues());
    };

    const createRows = () => {
        return stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <ClaimsForecastingTableRow
                key={el.id}
                row={el}
                roles={roles}
                toggleDropdown={onToggleDropdown}
                openId={openId}
                onStatusRowChange={onStatusRowChange}
            />
        ));
    };

    // EVENT HANDLERS
    const onToggleDropdown = (id) => setOpenId(id);

    // EVENT HANDLER CALLBACKS
    const onStatusChangeEditor = (isOpen) => {
        setIsStatusEditorOpen(isOpen);
    };

    const onStatusRowChange = (row) => {
        setStatusRow(row);
        setClaimQueue(claimQueues.find((el) => el.eventTypeId === row.eventTypeId) || {});
        setClaimEvent(claimEvents.find((el) => el.claimTypeId === row.eventTypeId) || {});
        setIsStatusEditorOpen(true);
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    // RENDER
    return (
        <div className={local.sectionWrapper} data-testid="claim_forecasting_table_management">
            <h2 className={app.sectionHeading}>Participant Claims</h2>
            {rows.length < 1 ? (
                <div>No claim forecasts found</div>
            ) : (
                <div>
                    <ResultsTable
                        defaultOrderBy={rowMetaData.orderBy}
                        sortOrder={rowMetaData.order}
                        defaultRowsPerPage={DEFAULT_TABLE_ROWS_PER_PAGE}
                        headCells={headCells}
                        loadResults={claimQueueForecast}
                        passRowMetaDataUp={setRowMetaData}
                        tableRows={createRows()}
                        totalResults={rows.length}
                    />
                </div>
            )}
            <StatusEditor
                open={isStatusEditorOpen}
                onStatusChangeEditor={onStatusChangeEditor}
                statusRow={statusRow}
                claimEventStatuses={claimEventStatuses}
                claimEventStatusReasons={claimEventStatusReasons}
                claimQueue={claimQueue}
                claimEvent={claimEvent}
            />
        </div>
    );
};

export default ClaimsForecastingTableManagement;
